import {
  createSlice,
  createAsyncThunk,
  PayloadAction,
} from "@reduxjs/toolkit";

import {
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
  collection,
  onSnapshot,
  query,
  where,
  getDocs,
} from "firebase/firestore";

import { db } from "../firebase";
import { AppDispatch, RootState } from "./store";

/* ─────────────────── TYPES ─────────────────── */

export interface ProductOrder {
  id:              string;
  guestId:         string;
  hostId:          string;
  items:           any[];
  subtotal:        number;
  totalQty:        number;
  totalPaid:       number;
  guestsFee:       number;
  hostsFee:        number;
  hostProfit:      number;
  platformProfit:  number;
  paymentId:       string;
  isConfirmed:     boolean;
  isCancelled:     boolean;
  isRefunded:      boolean;
  isPaid:          boolean;
  isDelivered:     boolean;
  createdAt?:      any;
  updatedAt?:      any;
  [key: string]:   any;
}

interface ProductOrdersState {
  orders:               ProductOrder[];
  currentOrder:         ProductOrder | null;
  error:                string | null;
  unsubscribe?:         () => void;
  hostDeliveredCount:   number;
  hostDeliveredProfit:  number;
}

/* ─────────────────── INITIAL STATE ─────────────────── */

const initialState: ProductOrdersState = {
  orders:              [],
  currentOrder:        null,
  error:               null,
  unsubscribe:         undefined,
  hostDeliveredCount:  0,
  hostDeliveredProfit: 0,
};

/* ─────────────────── UPSERT ORDER ─────────────────── */

export const upsertProductOrder = createAsyncThunk<
  ProductOrder,
  Partial<ProductOrder> & {
    paymentId: string;
    hostId?: string;
    listingId?: string;
    deliveryStatus?: {
      sellerDelivered?:  boolean;
      buyerReceived?:    boolean;
      orderDelivered?:   boolean;
    };
  },
  { rejectValue: string }
>(
  "productOrders/upsert",
  async (orderData, { rejectWithValue }) => {
    try {
      if (!orderData.paymentId) throw new Error("Missing paymentId");

      const docRef = doc(db, "ProductOrders", orderData.paymentId);
      const snap   = await getDoc(docRef);
      if (!snap.exists()) throw new Error("Order does not exist");

      const existing     = snap.data() as ProductOrder;
      const updatePayload: any = { updatedAt: serverTimestamp() };

      /* ── update item delivery statuses ── */
      if (orderData.deliveryStatus) {
        const updatedItems = existing.items.map((item: any) => {

          /* host marking shipped */
          if (
            orderData.hostId &&
            item.hostId === orderData.hostId &&
            orderData.deliveryStatus?.sellerDelivered === true
          ) {
            return { ...item, deliveryStatus: { ...item.deliveryStatus, sellerDelivered: true } };
          }

          /* guest marking received */
          if (
            orderData.listingId &&
            item.listingId === orderData.listingId &&
            orderData.deliveryStatus?.buyerReceived === true
          ) {
            return { ...item, deliveryStatus: { ...item.deliveryStatus, buyerReceived: true, orderDelivered: true } };
          }

          return item;
        });

        updatePayload.items = updatedItems;

        /* mark whole order delivered if all items done */
        const allDelivered = updatedItems.every(
          (i: any) => i.deliveryStatus?.sellerDelivered && i.deliveryStatus?.buyerReceived
        );
        if (allDelivered) updatePayload.isDelivered = true;
      }

      await updateDoc(docRef, updatePayload);
      return { ...existing, ...updatePayload, id: orderData.paymentId };
    } catch (err: any) {
      return rejectWithValue(err.message || "Failed to upsert product order");
    }
  }
);

/* ─────────────────── LISTENERS ─────────────────── */

/* Listen to a single order */
export const listenToProductOrder =
  (orderId: string) =>
  (dispatch: AppDispatch) => {
    const docRef = doc(db, "ProductOrders", orderId);

    const unsub = onSnapshot(docRef, (snap) => {
      if (snap.exists()) {
        const { id: _, ...rest } = snap.data() as ProductOrder;
        dispatch(productOrdersSlice.actions.setCurrentOrder({
          id: snap.id, ...rest,
          createdAt: rest.createdAt?.toMillis?.() ?? null,
          updatedAt: rest.updatedAt?.toMillis?.() ?? null,
        }));
      } else {
        dispatch(productOrdersSlice.actions.setCurrentOrder(null));
      }
    }, (err) => dispatch(productOrdersSlice.actions.setError(err.message)));

    dispatch(productOrdersSlice.actions.setUnsubscribe(unsub));
  };

/* Listen to guest orders */
export const listenToGuestProductOrders =
  (guestId: string) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    if (!guestId) return;
    const { unsubscribe } = getState().productOrders;
    if (unsubscribe) return;

    const q = query(
      collection(db, "ProductOrders"),
      where("guestId", "==", guestId)
    );

    const unsub = onSnapshot(q, (snap) => {
      const orders: ProductOrder[] = snap.docs.map(d => {
        const { id: _, ...rest } = d.data() as ProductOrder;
        return { id: d.id, ...rest, createdAt: rest.createdAt?.toMillis?.() ?? null, updatedAt: rest.updatedAt?.toMillis?.() ?? null };
      });
      dispatch(productOrdersSlice.actions.setOrders(orders));
    }, (err) => dispatch(productOrdersSlice.actions.setError(err.message)));

    dispatch(productOrdersSlice.actions.setUnsubscribe(unsub));
  };

/* Listen to host orders — only confirmed, only this host's items */
export const listenToHostProductOrders =
  (hostId: string) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    if (!hostId) return;
    const { unsubscribe } = getState().productOrders;
    if (unsubscribe) return;

    const q = query(
      collection(db, "ProductOrders"),
      where("isConfirmed", "==", true)
    );

    const unsub = onSnapshot(q, (snap) => {
      const orders: ProductOrder[] = snap.docs
        .map(d => {
          const { id: _, ...rest } = d.data() as ProductOrder;
          const hostItems = (rest.items ?? []).filter((i: any) => i.hostId === hostId);
          if (!hostItems.length) return null;
          return { id: d.id, ...rest, items: hostItems, createdAt: rest.createdAt?.toMillis?.() ?? null, updatedAt: rest.updatedAt?.toMillis?.() ?? null };
        })
        .filter(Boolean) as ProductOrder[];

      dispatch(productOrdersSlice.actions.setOrders(orders));
    }, (err) => dispatch(productOrdersSlice.actions.setError(err.message)));

    dispatch(productOrdersSlice.actions.setUnsubscribe(unsub));
  };

/* ─────────────────── HOST STATS ─────────────────── */

export const fetchHostProductOrderStats = createAsyncThunk<
  { total: number; delivered: number; pending: number; profit: number },
  string,
  { rejectValue: string }
>(
  "productOrders/fetchHostStats",
  async (hostId, { rejectWithValue }) => {
    try {
      if (!hostId) throw new Error("Missing hostId");
      const q    = query(collection(db, "ProductOrders"), where("hostId", "==", hostId));
      const snap = await getDocs(q);
      let total = 0, delivered = 0, pending = 0, profit = 0;
      snap.forEach(d => {
        const data = d.data() as ProductOrder;
        total++;
        if (data.isDelivered) { delivered++; profit += data.hostProfit ?? 0; }
        else pending++;
      });
      return { total, delivered, pending, profit };
    } catch (err: any) {
      return rejectWithValue(err.message || "Failed to fetch host stats");
    }
  }
);

/* ─────────────────── SLICE ─────────────────── */

const productOrdersSlice = createSlice({
  name: "productOrders",
  initialState,
  reducers: {
    setOrders:      (state, a: PayloadAction<ProductOrder[]>)      => { state.orders = a.payload; },
    setCurrentOrder:(state, a: PayloadAction<ProductOrder | null>) => { state.currentOrder = a.payload; },
    setError:       (state, a: PayloadAction<string | null>)       => { state.error = a.payload; },
    setUnsubscribe: (state, a: PayloadAction<(()=>void)|undefined>) => { state.unsubscribe = a.payload; },
    clearOrders:    (state) => { state.orders = []; state.currentOrder = null; },
  },
  extraReducers: builder => {
    builder
      .addCase(upsertProductOrder.fulfilled, (state, a) => {
        const idx = state.orders.findIndex(o => o.id === a.payload.id);
        if (idx >= 0) {
          state.orders[idx] = a.payload;
          if (state.currentOrder?.id === a.payload.id) state.currentOrder = a.payload;
        } else {
          state.orders.unshift(a.payload);
        }
      })
      .addCase(upsertProductOrder.rejected, (state, a) => {
        state.error = a.payload ?? "Failed to upsert product order";
      })
      .addCase(fetchHostProductOrderStats.fulfilled, (state, a) => {
        state.hostDeliveredCount  = a.payload.delivered;
        state.hostDeliveredProfit = a.payload.profit;
      });
  },
});

export const {
  setOrders,
  setCurrentOrder,
  setError,
  setUnsubscribe,
  clearOrders,
} = productOrdersSlice.actions;

export default productOrdersSlice.reducer;
